import React from 'react';
import { SocialIconsBlock as SocialIconsBlockType, SocialIcon } from './types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus, X } from 'lucide-react';

const platformOptions = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'X (Twitter)' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'tiktok', label: 'TikTok' },
];

// Map platform to actual PNG icon path (same as email output for WYSIWYG)
const getSocialIconPath = (platform: string): string => {
  const iconMap: Record<string, string> = {
    facebook: '/icons/social/facebook.png',
    instagram: '/icons/social/instagram.png',
    linkedin: '/icons/social/linkedin.png',
    twitter: '/icons/social/twitter.png',
    youtube: '/icons/social/youtube.png',
    tiktok: '/icons/social/tiktok.png',
    whatsapp: '/icons/social/whatsapp.png',
  };
  return iconMap[platform] || '';
};

interface SocialIconsBlockProps {
  block: SocialIconsBlockType;
  onChange: (block: SocialIconsBlockType) => void;
  isEditing?: boolean;
}

// Render platform icon using the same PNGs as email output (true WYSIWYG)
const PlatformIcon: React.FC<{ platform: string; size: number; style: string }> = ({ platform, size, style }) => {
  const iconPath = getSocialIconPath(platform);
  
  if (!iconPath) {
    return (
      <div 
        className="flex items-center justify-center rounded-full bg-muted"
        style={{ width: size, height: size, fontSize: size * 0.5 }}
      >
        {platform.charAt(0).toUpperCase()}
      </div>
    );
  }

  if (style === 'circle') {
    const containerSize = size + 8;
    return (
      <div 
        className="flex items-center justify-center rounded-full"
        style={{ 
          width: containerSize, 
          height: containerSize, 
          backgroundColor: '#f0f0f0',
        }}
      >
        <img 
          src={iconPath} 
          alt={platform} 
          width={size * 0.7} 
          height={size * 0.7} 
          style={{ display: 'block' }}
        />
      </div>
    );
  }

  return (
    <img 
      src={iconPath} 
      alt={platform} 
      width={size} 
      height={size} 
      style={{ display: 'block' }}
    />
  );
};

export const SocialIconsBlockDisplay: React.FC<{ block: SocialIconsBlockType }> = ({ block }) => {
  if (block.icons.length === 0) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground/50 italic text-sm">
        No social icons added
      </div>
    );
  }

  return (
    <div className="flex items-center" style={{ gap: block.spacing }}>
      {block.icons.map((icon, index) => (
        <a 
          key={index} 
          href={icon.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="hover:opacity-80 transition-opacity"
        >
          <PlatformIcon platform={icon.platform} size={block.iconSize} style={block.iconStyle} />
        </a>
      ))}
    </div>
  );
};

export const SocialIconsBlockEditor: React.FC<SocialIconsBlockProps> = ({ block, onChange }) => {
  const addIcon = () => {
    const newIcon: SocialIcon = { platform: 'linkedin', url: '' };
    onChange({ ...block, icons: [...block.icons, newIcon] });
  };

  const updateIcon = (index: number, updates: Partial<SocialIcon>) => {
    const newIcons = [...block.icons];
    newIcons[index] = { ...newIcons[index], ...updates };
    onChange({ ...block, icons: newIcons });
  };

  const removeIcon = (index: number) => {
    onChange({ ...block, icons: block.icons.filter((_, i) => i !== index) });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Social Icons</Label>
        {block.icons.length === 0 ? (
          <p className="text-sm text-muted-foreground">No icons added yet</p>
        ) : (
          <div className="space-y-3">
            {block.icons.map((icon, index) => (
              <div key={index} className="flex items-center gap-2 p-2 border rounded-md">
                <Select
                  value={icon.platform}
                  onValueChange={(value: SocialIcon['platform']) => updateIcon(index, { platform: value })}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {platformOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  value={icon.url}
                  onChange={(e) => updateIcon(index, { url: e.target.value })}
                  placeholder="https://..."
                  className="flex-1"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeIcon(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={addIcon}>
          <Plus className="h-4 w-4 mr-2" />
          Add Social Icon
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Icon Size (px)</Label>
          <Input
            type="number"
            value={block.iconSize}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, iconSize: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, iconSize: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 1) onChange({ ...block, iconSize: 24 });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Spacing (px)</Label>
          <Input
            type="number"
            value={block.spacing}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, spacing: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, spacing: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 0) onChange({ ...block, spacing: 8 });
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Icon Style</Label>
        <Select
          value={block.iconStyle}
          onValueChange={(value: 'color' | 'monochrome' | 'circle') => onChange({ ...block, iconStyle: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="color">Full Color</SelectItem>
            <SelectItem value="monochrome">Monochrome</SelectItem>
            <SelectItem value="circle">Circle Background</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
